import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Cost } from 'src/app/shared/models/cost.model';
import { CustomValidators } from 'src/app/shared/directives/custom-validator.directive';
import {
  CostAdditionalCodes,
  convertChecboxToCostAdditionalCodesArray
} from 'src/app/shared/models/cost-additional-code.model';
import { Employee } from 'src/app/shared/models/employee.model';
import { CostService } from 'src/app/shared/services/cost.service';
import { UserService } from 'src/app/shared/services/user.service';
import { shareReplay, map, tap, filter, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { EmployeeService } from 'src/app/shared/services/employee.service';
import { ConfirmService } from '../../../shared/services/confirm.service';
import { Advance } from '../../../shared/models/advance.model';
import { KSEF_NUMBER_PATTERN, formatKsefNumber } from 'src/app/shared/utils/ksef.util';
import { KsefApiService } from 'src/app/main/ksef/services/ksef-api.service';

@Component({
  selector: 'app-cost-add',
  templateUrl: './cost-add.component.html'
})
export class CostAddComponent implements OnInit {

  cost: Cost;
  costForm: FormGroup;
  currentUserId: number;
  currentUser: Employee;
  ksefId: number | null = null;
  formattedDateUpdatedAt;
  formattedDateCreatedAt;
  originalInvoiceNameRequired$;
  employees: Employee[];
  ksefAttachmentUrl: string | null = null;

  employees$;
  costTypesPossibleToRegister$;
  advances: Advance[];
  advances$;
  costAdditionalCodes = CostAdditionalCodes;
  hasCreatedThisCost;
  authEmployeeRoles;

  selectsLoading = {
    advance: true,
    type: true,
    employee: true,
  };

  constructor(
    private _router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private costService: CostService,
    private userService: UserService,
    private toastrService: ToastrService,
    private employeeService: EmployeeService,
    private confirmService: ConfirmService,
    private ksefApi: KsefApiService,
  ) {
  }

  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    this.authEmployeeRoles = this.currentUser.user.roles;
    this.currentUserId = this.currentUser.id;
    this.createFormGroup();
    this.setupOriginalInvoiceNameRequired();
    this.setupKsefNumberValidationAndFormat();

    const ksefIdParam = this.route.snapshot.queryParamMap.get('ksefId');
    if (ksefIdParam && /^\d+$/.test(ksefIdParam)) {
      this.ksefId = parseInt(ksefIdParam, 10);
      this.ksefApi.getInvoice(this.ksefId).subscribe(
        (inv) => {
          this.costForm.patchValue({
            name: (inv.contractorName || inv.ksefNumber || 'KSeF').substring(0, 15),
            docNumber: inv.vendorInvoiceNumber || inv.ksefNumber,
            docDate: inv.issueDate ? inv.issueDate.substring(0, 10) : null,
            grossValue: inv.grossAmount != null ? inv.grossAmount : null,
            companyName: inv.contractorName || null,
            isKsef: true,
            ksefNumber: inv.ksefNumber,
            isDocumentElectronic: 1,
          });
          this.ksefAttachmentUrl = inv.attachmentUrl || null;
        },
        () => this.toastrService.warning('Nie udało się pobrać danych faktury KSeF.')
      );
    }

    this.advances$ = this.employeeService.fetchAdvances(this.currentUserId).pipe(
      tap(advances => this.advances = advances),
      tap(v => this.selectsLoading.advance = false),
    );
    this.costTypesPossibleToRegister$ = this.employeeService.fetchCostTypesPossibleToRegister(this.currentUserId).pipe(
      tap(v => this.selectsLoading.type = false),
      map(costTypes => costTypes.filter(v => v.name !== 'delegation')),
    );

    this.employees$ = this.employeeService.fetchEmployeesByCostType(-1).pipe(
      tap(v => this.selectsLoading.employee = false)
    );
  }

  createFormGroup() {

    this.costForm = this.fb.group({
      name: [null, [Validators.required]],
      advance: [null, [Validators.required]],
      grossValue: [null, [Validators.required]],
      docNumber: [null, [Validators.required]],
      docDate: [null, [Validators.required]],
      isDocumentElectronic: [null, [Validators.required]],
      companyName: [],
      machineModel: [],
      customer: [],
      pono: [],
      delegationNumber: [],
      machineSerialNumber: [],
      detailedCode: [],
      description: [null, [Validators.required]],
      isReinvoicing: [],
      originalInvoiceName: [],
      additionalCodes: this.fb.group({
        YmcePlExpense: false,
        Sale: false,
        Warehouse: false,
        Tests: false
      }, {
        validator: [CustomValidators.multipleCheckboxRequireOne]
      }),
      type: [null, [Validators.required]],
      employee: [this.currentUserId],
      note: '',
      isKsef: [null, Validators.required],
      ksefNumber: [null]
    });
  }

  setupOriginalInvoiceNameRequired() {
    this.originalInvoiceNameRequired$ = this.costForm.get('isReinvoicing').valueChanges.pipe(
      map(value => {
        /*
         * If IsReinvoicing is selected, no additional codes are required
         */
        if (value) {
          this.costForm.get('originalInvoiceName').setValidators(Validators.required)
          this.costForm.get('additionalCodes').setValidators([]);
        } else {
          this.costForm.get('additionalCodes').setValidators(CustomValidators.multipleCheckboxRequireOne);
          this.costForm.get('originalInvoiceName').setValidators([]);
        }

        this.costForm.get('additionalCodes').updateValueAndValidity();
        this.costForm.get('originalInvoiceName').updateValueAndValidity();

        return value;
      }),
      shareReplay()
    );
  }

  setupKsefNumberValidationAndFormat() {
    const isKsefCtrl = this.costForm.get('isKsef');
    const ksefNumberCtrl = this.costForm.get('ksefNumber');

    isKsefCtrl.valueChanges.pipe(
      tap(isKsef => {
        if (isKsef) {
          ksefNumberCtrl.setValidators([Validators.required, Validators.pattern(KSEF_NUMBER_PATTERN)]);
        } else {
          ksefNumberCtrl.setValidators(null);
          ksefNumberCtrl.setValue(null, { emitEvent: false });
        }
        ksefNumberCtrl.updateValueAndValidity();
      })
    ).subscribe();

    ksefNumberCtrl.valueChanges.pipe(
      filter(() => isKsefCtrl.value === true),
      map(raw => formatKsefNumber(raw)),
      tap(formatted => {
        if (ksefNumberCtrl.value !== formatted) {
          ksefNumberCtrl.setValue(formatted, { emitEvent: false });
        }
      })
    ).subscribe();

    if (isKsefCtrl.value) {
      ksefNumberCtrl.setValidators([Validators.required, Validators.pattern(KSEF_NUMBER_PATTERN)]);
      ksefNumberCtrl.updateValueAndValidity();
    }
  }

  createCost() {
    const [advance] = this.advances.filter(v => v.id === this.costForm.get('advance').value);
    if (!advance) {
      this.toastrService.error('Musisz wybrać zaliczkę.');
      return;
    }

    this.confirmService.openConfirmDialog('Wybrałeś zaliczkę `' + advance.name + '`. Czy na pewno chcesz kontynuować?').afterClosed()
      .subscribe((confirmation: boolean) => {
        const transformedForm = {
          ...this.costForm.value,
          additionalCodes: convertChecboxToCostAdditionalCodesArray(this.costForm.value.additionalCodes),
        };
        const cost = Cost.adapt(transformedForm);
        if (typeof cost.docNumber === 'string') {
          cost.docNumber = cost.docNumber.trim();
        } else if (cost.docNumber != null) {
          cost.docNumber = String(cost.docNumber).trim();
        } else {
          cost.docNumber = '';
        }
        if (this.ksefId) {
          (cost as { ksefId?: number }).ksefId = this.ksefId;
        }

        if (confirmation) {
          this.costService.createCost(cost).subscribe((response: { cost: { id: number } }) => {
            this._router.navigate([`cost/${response.cost.id}`]);
            this.toastrService.success('Poprawnie utworzono koszt.');
          }, err => this.toastrService.error((err && err.error && err.error.message) || 'Nie udało się utworzyć kosztu.'));
        }
      });
  }

}
